/**
 * Provider refusals in the person's words (M4.3, moved to core in M5.3 so the API and the
 * web compose the same line): "Microsoft refused: insufficient permissions (403)" and the one
 * action after it. Never troubleshooting steps.
 */
export type ProviderLang = 'en' | 'ar';

export interface ProviderFailureInfo {
  provider: string;
  status?: number | undefined;
  reason?: string | undefined;
  detail?: string | undefined;
  endpoint?: string | undefined;
  transient?: boolean | undefined;
  /** The one action that fixes it, when known: reconnect, or connect another account for mail. */
  fix?: 'reconnect' | 'connect' | undefined;
}

export const PROVIDER_NAME: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };

export const PROVIDER_REASONS: Record<string, { en: string; ar: string }> = {
  insufficientpermissions: { en: 'insufficient permissions', ar: 'صلاحية غير كافية' },
  insufficient_scope: { en: 'insufficient permissions', ar: 'صلاحية غير كافية' },
  permission_denied: { en: 'insufficient permissions', ar: 'صلاحية غير كافية' },
  erroraccessdenied: { en: 'insufficient permissions', ar: 'صلاحية غير كافية' },
  accessnotconfigured: {
    en: 'Gmail API not enabled on the project',
    ar: 'واجهة Gmail غير مفعّلة في المشروع',
  },
  failedprecondition: {
    en: 'this account has no mailbox',
    ar: 'لا يوجد صندوق بريد في هذا الحساب',
  },
  invalid_grant: { en: 'sign-in expired', ar: 'انتهت صلاحية الدخول' },
  unauthorized: { en: 'sign-in expired', ar: 'انتهت صلاحية الدخول' },
  notfound: { en: 'not found', ar: 'غير موجود' },
  not_found: { en: 'not found', ar: 'غير موجود' },
  rate_limited: { en: 'too many requests', ar: 'طلبات كثيرة' },
  ratelimitexceeded: { en: 'too many requests', ar: 'طلبات كثيرة' },
  transient: { en: 'a temporary error', ar: 'خطأ مؤقت' },
};

/** "Google refused: insufficient permissions (403)" / "رفض Google: صلاحية غير كافية (403)" */
export function providerRefusedLine(f: ProviderFailureInfo, lang: ProviderLang): string {
  const name = PROVIDER_NAME[f.provider] ?? f.provider;
  const key = (
    f.reason ?? (f.status === 401 ? 'unauthorized' : f.transient ? 'transient' : '')
  ).toLowerCase();
  const fallback = { en: 'a temporary error', ar: 'خطأ مؤقت' };
  const reason = PROVIDER_REASONS[key] ?? (f.reason ? { en: f.reason, ar: f.reason } : fallback);
  const status = f.status ? ` (${f.status})` : '';
  return lang === 'ar'
    ? `رفض ${name}: ${reason.ar}${status}`
    : `${name} refused: ${reason.en}${status}`;
}

/** The short line for a repeated refusal (M5.3): "Microsoft refused again (403)." */
export function providerRefusedAgainLine(f: ProviderFailureInfo, lang: ProviderLang): string {
  const name = PROVIDER_NAME[f.provider] ?? f.provider;
  const status = f.status ? ` (${f.status})` : '';
  return lang === 'ar' ? `رفض ${name} مرة أخرى${status}.` : `${name} refused again${status}.`;
}

/** The one action after a refusal: reconnect, or nothing more to do. */
export function providerActionLine(f: ProviderFailureInfo, lang: ProviderLang): string {
  const name = PROVIDER_NAME[f.provider] ?? f.provider;
  if (f.fix === 'reconnect') return lang === 'ar' ? `أعد ربط ${name}.` : `Reconnect ${name}.`;
  if (f.fix === 'connect') {
    return lang === 'ar' ? 'اربط حساباً آخر للبريد.' : 'Connect another account for mail.';
  }
  if ((f.reason ?? '').toLowerCase() === 'accessnotconfigured') {
    return lang === 'ar'
      ? 'فعّل واجهة Gmail في مشروع Google Cloud ثم حاول مرة أخرى.'
      : 'Enable the Gmail API on the Google Cloud project, then try again.';
  }
  return lang === 'ar'
    ? 'أخبرني إذا أردت أن أحاول لاحقاً.'
    : 'Say the word and I will try again later.';
}

/** "Draft to m@x: Microsoft refused, insufficient permissions (403)" for a checklist. */
export function failedChecklistLine(
  what: string,
  f: ProviderFailureInfo,
  lang: ProviderLang,
): string {
  const refused = providerRefusedLine(f, lang).replace(
    /^([^:]+): /u,
    lang === 'ar' ? '$1، ' : '$1, ',
  );
  return `${what}: ${refused}`;
}
